import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import _ from 'lodash';
import { AnnotationFlags, PDFArray, PDFDocument, PDFHexString, PDFName, PDFString, rgb } from 'pdf-lib';
import { getDocument } from 'pdfjs-dist';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import apiService from 'services/api';
import { Box, Button, IconButton, ProgressBar, ResponsiveDialog, Stack, styled, Text } from 'shell/components';
import { uploadFile } from 'utils/file';
import { extractFeedbackPlainAndHtml, getS3FileURL, stripHTML } from 'utils/helper';

const ConfirmPushGradesDialog = ({ open, onClose, assignment, submissions, match = true }) => {
    const { auth } = useAuth();
    const { t } = useTranslation('assignment');
    const { message } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [filteredLoading, setFilteredLoading] = useState(false);
    const [isSucceed, setIsSucceed] = useState(false);
    const [progress, setProgress] = useState(0);
    const [percentage, setPercentage] = useState(0);
    const [filteredSubmisisons, setFilteredSubmissions] = useState(null);

    const stripAngleBracketContent = (input) => {
        if (!input) return '';
        return input.replace(/<[^>]*>/g, '').trim();
    };

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            setIsSucceed(false);
            setProgress(0);
            setPercentage(0);
        }, 500);
    };

    // Helper function to introduce a delay
    const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    function findTextMatchIndex(fullText, targetText) {
        const positionMap = [];
        const normalizedFull = fullText
            .split('')
            .filter((char, i) => {
                if (!/\s/.test(char)) {
                    positionMap.push(i);
                    return true;
                }
                return false;
            })
            .join('')
            .toLowerCase();
        const normalizedTarget = targetText.replace(/\s+/g, '').toLowerCase();
        const matchIndex = normalizedFull.indexOf(normalizedTarget);
        if (matchIndex === -1) return null;
        const originalStart = positionMap[matchIndex];
        const originalEnd = positionMap[matchIndex + normalizedTarget.length - 1];
        return { start: originalStart, end: originalEnd + 1 };
    }

    async function findCoordinatesForTextMatch(pdf, pageNumber, targetText) {
        const page = await pdf.getPage(Number(pageNumber));
        const textContent = await page.getTextContent();
        const textItems = textContent.items;

        const fullText = textItems.map((t) => t.str).join('');
        const match = findTextMatchIndex(fullText, targetText);
        if (!match) return null;

        const { start: matchStart, end: matchEnd } = match;

        let charIndex = 0;
        const matchedItems = [];

        for (const item of textItems) {
            const itemStr = item.str;
            const itemStart = charIndex;
            const itemEnd = charIndex + itemStr.length;

            if (itemEnd > matchStart && itemStart < matchEnd) {
                matchedItems.push(item);
            }

            charIndex += itemStr.length;
        }

        if (matchedItems.length === 0) return null;

        return matchedItems.map((item) => {
            const [, , , fontHeight, x, y] = item.transform;
            return {
                x,
                y: y - Math.abs(fontHeight) * 0.3,
                width: item.width ?? 0,
                height: Math.abs(fontHeight) * 1.3,
            };
        });
    }

    const convertPDFwithInlineComments = async (url, inlineComments) => {
        const res = await fetch(url);
        let fileArrayBuffer = await res.arrayBuffer();
        let filename = 'submission.pdf';
        try {
            filename = decodeURI(new URL(url).pathname.split('/').pop() || filename);
        } catch {}

        // Keep the loading task so we can destroy it later
        const loadingTask = getDocument({ data: fileArrayBuffer });
        const pdf = await loadingTask.promise;
        const pdfDoc = await PDFDocument.load(fileArrayBuffer);

        try {
            for (const comment of inlineComments) {
                if (!comment?.active || !comment?.highlight) continue;
                let coords;
                try {
                    coords = await findCoordinatesForTextMatch(pdf, comment.page_num, comment.highlight);
                } catch (e) {
                    console.error(e);
                    continue;
                }
                if (!coords?.length) continue;

                const page = pdfDoc.getPage(Number(comment.page_num - 1));
                const EPSILON = 0.5;

                for (let i = 0; i < coords.length; i++) {
                    const item = coords[i];
                    const prev = coords[i - 1];
                    const prevEnd = prev ? prev.x + prev.width : 0;
                    const sameLine = !prev || Math.abs(prev.y - item.y) < 2;
                    const overlap = sameLine && prevEnd - item.x > EPSILON ? prevEnd - item.x : 0;
                    const adjustedWidth = Math.max(0, item.width - overlap);
                    if (adjustedWidth > 0) {
                        page.drawRectangle({
                            x: item.x + overlap,
                            y: item.y,
                            width: adjustedWidth,
                            height: item.height,
                            color: rgb(0, 0.78, 0.33),
                            opacity: 0.25,
                        });
                    }
                }

                const first = coords[0];
                const annotationRef = pdfDoc.context.nextRef();
                const annotationDict = pdfDoc.context.obj({
                    Type: PDFName.of('Annot'),
                    Subtype: PDFName.of('Text'),
                    Rect: pdfDoc.context.obj([first.x, first.y + first.height, first.x + 20, first.y + 20]),
                    Contents: PDFHexString.fromText(comment.content || ''),
                    Name: PDFName.of('Comment'),
                    Flags: pdfDoc.context.obj(AnnotationFlags.Print),
                    M: PDFString.fromDate(new Date()),
                });
                pdfDoc.context.assign(annotationRef, annotationDict);
                const pageNode = page.node;
                const annots = pageNode.lookup(PDFName.of('Annots'), PDFArray);
                if (annots) annots.push(annotationRef);
                else pageNode.set(PDFName.of('Annots'), pdfDoc.context.obj([annotationRef]));
            }

            const modifiedPdfBytes = await pdfDoc.save();
            const blob = new Blob([modifiedPdfBytes], { type: 'application/pdf' });
            const file = new File([blob], filename);
            return await new Promise((resolve, reject) => {
                uploadFile(
                    file,
                    () => {},
                    (data) => {
                        const s3 = getS3FileURL(data);
                        s3 ? resolve(s3) : reject(new Error('No S3 URL'));
                    },
                    (err) => reject(err),
                    auth.owner_account_id
                );
            });
        } finally {
            try {
                await pdf.cleanup?.();
            } catch {}
            try {
                await pdf.destroy?.();
            } catch {} // kills the document + worker :contentReference[oaicite:1]{index=1}
            try {
                await loadingTask.destroy?.();
            } catch {} // extra safety if your pdf.js version needs it :contentReference[oaicite:2]{index=2}
            fileArrayBuffer = null; // drop big references for GC
        }
    };

    const pushGradeToLMS = async (accessToken, course_id, submission, data, hasOutOfZero = false, tries = 0) => {
        const maxTries = 20;
        const baseDelay = 500; // Base delay in milliseconds for exponential backoff

        for (let attempt = 0; attempt <= maxTries; attempt++) {
            try {
                // Attempt to post the grade
                return await apiService.postGrade({
                    course_id,
                    accessToken,
                    user_id: submission.api.user.id,
                    assignment_id: data.api_data.id,
                    submission,
                    assignment: data,
                    match,
                    hasOutOfZero,
                });
            } catch (error) {
                // Extract response details if available
                const { response } = error;

                // Determine if the error is due to rate limiting
                if (response && response.status === 429) {
                    // Extract Retry-After header (in seconds)
                    const retryAfter = response.headers['retry-after'];
                    let waitTime = 0;

                    if (retryAfter) {
                        // Convert Retry-After to milliseconds
                        waitTime = parseInt(retryAfter, 10) * 1000;
                    } else {
                        // If Retry-After is not provided, use exponential backoff
                        waitTime = baseDelay * Math.pow(2, attempt);
                    }

                    // Apply jitter: random value between 0 and 100 milliseconds
                    const jitter = Math.floor(Math.random() * 100);
                    waitTime += jitter;

                    if (attempt === maxTries) {
                        message.error(
                            t('common:errors.rate-limit-exceeded', 'Rate limit exceeded. Please try again later.')
                        );
                        console.error(
                            `Failed to push grade to LMS after ${maxTries} attempts due to rate limiting:`,
                            error
                        );
                        throw error; // Optionally rethrow the error to be handled upstream
                    }

                    // Informational logging
                    console.warn(`Rate limit hit. Retrying in ${waitTime}ms... (Attempt ${attempt + 1}/${maxTries})`);

                    // Wait before retrying
                    await delay(waitTime);
                } else {
                    // Handle other types of errors (network issues, server errors, etc.)
                    const jitter = Math.floor(Math.random() * 100) + 1; // 1-50 ms
                    const waitTime = baseDelay * Math.pow(2, attempt) + jitter;

                    if (attempt === maxTries) {
                        message.error(t('common:message.something-went-wrong', 'Something went wrong.'));
                        console.error(`Failed to push grade to LMS after ${maxTries} attempts:`, error);
                        throw error; // Optionally rethrow the error to be handled upstream
                    }

                    // Informational logging
                    console.warn(
                        `Error encountered. Retrying in ${waitTime}ms... (Attempt ${attempt + 1}/${maxTries})`
                    );

                    // Wait before retrying
                    await delay(waitTime);
                }
            }
        }
    };

    const handleClickPush = async () => {
        const course_id = auth.course_id;
        const accessToken = localStorage.getItem('access_token');
        let tries = 0;
        if (course_id) {
            try {
                setLoading(true);
                setProgress(0); // Initialize progress

                const batchSize = 3; // Number of submissions to process concurrently
                for (let i = 0; i < submissions.length; i += batchSize) {
                    const batch = submissions.slice(i, i + batchSize).map((s) => _.cloneDeep(s));
                    await Promise.all(
                        batch.map(async (submission) => {
                            let feedback = '';
                            let criterion_feedback = null;
                            let commentary = '';
                            let hasOutOfZero = false;
                            if (submission.grading.feedback) {
                                if (typeof submission.grading.feedback === 'string') {
                                    feedback += extractFeedbackPlainAndHtml(submission.grading.feedback);
                                } else if (submission.grading.feedback.feedback_type === 'rubric') {
                                    criterion_feedback = {};
                                    submission.grading.feedback.improvement_suggestions.forEach(
                                        ({ criterion_id, positive, negative }, index) => {
                                            if (submission.grading.feedback.commentary) {
                                                commentary = stripHTML(submission.grading.feedback.commentary);
                                            }
                                            let cur_criterion_feedback = '';
                                            let rubric_feedback = '';
                                            let criterion = assignment.rubric.criteria.find(
                                                ({ id }) => id === criterion_id
                                            );
                                            const gradedOptionId = submission.grading.criteria[`${criterion_id}`];
                                            const option = criterion.options.find(
                                                (opt) =>
                                                    `${gradedOptionId}` === `${opt.id}` ||
                                                    gradedOptionId.includes(opt.id)
                                            );
                                            let isLowest = false;
                                            let isHighest = false;
                                            const sortedOptions = criterion.options.sort(
                                                (a, b) => Number(`${b.rate}`) - Number(`${a.rate}`)
                                            );
                                            const isOutOfZero = Number(sortedOptions[0].rate) === 0;
                                            if (isOutOfZero) {
                                                hasOutOfZero = true;
                                            }
                                            if (`${sortedOptions[0].rate}` === `${option.rate}`) {
                                                isHighest = true;
                                            }
                                            if (
                                                `${sortedOptions[sortedOptions.length - 1].rate}` === `${option.rate}`
                                            ) {
                                                isLowest = true;
                                            }
                                            cur_criterion_feedback += `${stripAngleBracketContent(criterion.title)}\n`;
                                            cur_criterion_feedback +=
                                                isLowest && !isOutOfZero
                                                    ? ''
                                                    : !positive.active
                                                      ? ''
                                                      : `${stripHTML(positive.text)}\n\n`;
                                            rubric_feedback +=
                                                isLowest && !isOutOfZero
                                                    ? ''
                                                    : !positive.active
                                                      ? ''
                                                      : `${stripHTML(positive.text)}\n\n`;

                                            cur_criterion_feedback +=
                                                isHighest && !isOutOfZero
                                                    ? ''
                                                    : !negative.active
                                                      ? ''
                                                      : `${stripHTML(negative.text)}`;
                                            rubric_feedback +=
                                                isHighest && !isOutOfZero
                                                    ? ''
                                                    : !negative.active
                                                      ? ''
                                                      : `${stripHTML(negative.text)}`;

                                            criterion_feedback[`${criterion_id}`] = rubric_feedback;
                                            feedback += cur_criterion_feedback;
                                            if (index) feedback += '\n\n';
                                        }
                                    );
                                } else if (submission.grading.feedback.feedback_type === 'system') {
                                    feedback += stripHTML(submission.grading.feedback.commentary);
                                    submission.grading.feedback.improvement_suggestions
                                        .filter(({ active }) => active)
                                        .forEach(({ text }) => {
                                            feedback += '\n\n';
                                            feedback += stripHTML(text);
                                        });
                                } else if (
                                    submission.grading.feedback.feedback_type === 'custom' ||
                                    submission.grading.feedback.feedback_type === 'templated'
                                ) {
                                    feedback += stripHTML(submission.grading.feedback.commentary);
                                }
                            }

                            submission.grading.feedback = feedback;
                            if (criterion_feedback) {
                                submission.criterion_feedback = criterion_feedback;
                            }
                            if (commentary) {
                                submission.grading.commentary = commentary;
                            }
                            if (submission.grading.inline_comments) {
                                let toInlineCommentedPdf = [];
                                let pdfsWithInlineComments = [];
                                const files = submission.files.filter((file) => file.type === 'application/pdf');
                                for (const file of files) {
                                    toInlineCommentedPdf.push({
                                        file: file,
                                        comments: submission.grading.inline_comments.filter(
                                            (c) => c.file_id === file.id && c.active
                                        ),
                                    });
                                }
                                if (toInlineCommentedPdf.length) {
                                    await Promise.all(
                                        toInlineCommentedPdf.map(async (item) => {
                                            const fileUrl = getS3FileURL(item.file);
                                            const pdfWithInlineComments = await convertPDFwithInlineComments(
                                                fileUrl,
                                                item.comments
                                            );
                                            pdfsWithInlineComments.push(pdfWithInlineComments);
                                        })
                                    );
                                }
                                if (pdfsWithInlineComments.length) {
                                    submission.pdfs_with_inline_comments = pdfsWithInlineComments;
                                }
                            }

                            // Await the pushGradeToLMS function with retry logic
                            await pushGradeToLMS(accessToken, course_id, submission, assignment, hasOutOfZero, tries);

                            // Update progress
                            setProgress((current) => current + 1);
                        })
                    );
                }

                setLoading(false);
                setIsSucceed(true);
                message.success(
                    t('common:message.grades-push-success', 'All grades have been successfully pushed to LMS.')
                );
            } catch (e) {
                console.error('Error pushing grades to LMS:', e);
                setLoading(false);

                message.error(
                    t('common:message.grades-push-fail', 'Failed to push some grades. Please try again later.')
                );
            }
        }
    };

    async function fetchAllCanvasSubmissions(courseId, assignmentId, accessToken) {
        const submissions = [];
        let url = null;

        do {
            const data = await apiService.getSubmissions({
                course_id: courseId,
                accessToken,
                assignment_id: assignmentId,
                url,
            });

            submissions.push(...data.submissions);
            // Parse Link header for next page URL
            url = null;
            if (data.has_next) {
                url = data.next_url;
                // (Or use manual parsing as shown above if not using the library)
            }
        } while (url);
        // Remove unsubmitted entries
        const results = submissions.filter((s) => s.workflow_state !== 'unsubmitted');
        return results;
    }

    const handleClickPushMissingGrades = async () => {
        const course_id = auth.course_id;
        const accessToken = localStorage.getItem('access_token');
        let tries = 0;
        setFilteredLoading(true);
        // Get submissions from the LMS API
        let apiSubmissions = await fetchAllCanvasSubmissions(course_id, `${assignment.api_data.id}`, accessToken);
        let gradedSubmissions = submissions;
        if (process.env.REACT_APP_KIND === 'CANVAS') {
            gradedSubmissions = apiSubmissions
                .filter((submission) => submission.workflow_state !== 'graded')
                .map((submission) => `${submission.user_id}`);
        } else {
            gradedSubmissions = apiSubmissions
                .filter((submission) => !submission.Feedback || !submission.Feedback.IsGraded)
                .map((submission) => `${submission.Entity.EntityId}`);
        }

        const filteredSubmisisons = submissions.filter((submission) =>
            gradedSubmissions.includes(`${submission.api.user.id}`)
        );
        setFilteredSubmissions(filteredSubmisisons);

        if (course_id) {
            try {
                setProgress(0); // Initialize progress

                const batchSize = 2; // Number of submissions to process concurrently
                for (let i = 0; i < filteredSubmisisons.length; i += batchSize) {
                    const clonedSubmissions = _.cloneDeep(filteredSubmisisons);
                    const batch = clonedSubmissions.slice(i, i + batchSize);
                    await Promise.all(
                        batch.map(async (submission) => {
                            let feedback = '';
                            let criterion_feedback = null;
                            let commentary = '';
                            let hasOutOfZero = false;
                            if (submission.grading.feedback) {
                                if (typeof submission.grading.feedback === 'string') {
                                    feedback += extractFeedbackPlainAndHtml(submission.grading.feedback);
                                } else if (submission.grading.feedback.feedback_type === 'rubric') {
                                    criterion_feedback = {};
                                    submission.grading.feedback.improvement_suggestions.forEach(
                                        ({ criterion_id, positive, negative }, index) => {
                                            if (index) feedback += '\n\n';
                                            if (submission.grading.feedback.commentary) {
                                                commentary = stripHTML(submission.grading.feedback.commentary);
                                            }
                                            let cur_criterion_feedback = '';
                                            let rubric_feedback = '';
                                            let criterion = assignment.rubric.criteria.find(
                                                ({ id }) => id === criterion_id
                                            );
                                            const gradedOptionId = submission.grading.criteria[`${criterion_id}`];
                                            const option = criterion.options.find((opt) =>
                                                gradedOptionId.includes(opt.id)
                                            );
                                            let isLowest = false;
                                            let isHighest = false;
                                            const sortedOptions = criterion.options.sort(
                                                (a, b) => Number(`${b.rate}`) - Number(`${a.rate}`)
                                            );
                                            const isOutOfZero = Number(sortedOptions[0].rate) === 0;
                                            if (isOutOfZero) {
                                                hasOutOfZero = true;
                                            }
                                            if (`${sortedOptions[0].rate}` === `${option.rate}`) {
                                                isHighest = true;
                                            }
                                            if (
                                                `${sortedOptions[sortedOptions.length - 1].rate}` === `${option.rate}`
                                            ) {
                                                isLowest = true;
                                            }
                                            cur_criterion_feedback += `${stripAngleBracketContent(criterion.title)}\n`;
                                            cur_criterion_feedback +=
                                                isLowest && !isOutOfZero
                                                    ? ''
                                                    : !positive.active
                                                      ? ''
                                                      : `${stripHTML(positive.text)}`;
                                            rubric_feedback +=
                                                isLowest && !isOutOfZero
                                                    ? ''
                                                    : !positive.active
                                                      ? ''
                                                      : `${stripHTML(positive.text)}`;

                                            cur_criterion_feedback +=
                                                isHighest && !isOutOfZero
                                                    ? ''
                                                    : !negative.active
                                                      ? ''
                                                      : `\n\n${stripHTML(negative.text)}`;
                                            rubric_feedback +=
                                                isHighest && !isOutOfZero
                                                    ? ''
                                                    : !negative.active
                                                      ? ''
                                                      : `\n\n${stripHTML(negative.text)}`;

                                            criterion_feedback[`${criterion_id}`] = rubric_feedback;
                                            feedback += cur_criterion_feedback;
                                        }
                                    );
                                } else if (submission.grading.feedback.feedback_type === 'system') {
                                    feedback += stripHTML(submission.grading.feedback.commentary);
                                    submission.grading.feedback.improvement_suggestions
                                        .filter(({ active }) => active)
                                        .forEach(({ text }) => {
                                            feedback += '\n\n';
                                            feedback += stripHTML(text);
                                        });
                                } else if (
                                    submission.grading.feedback.feedback_type === 'custom' ||
                                    submission.grading.feedback.feedback_type === 'templated'
                                ) {
                                    feedback += stripHTML(submission.grading.feedback.commentary);
                                }
                            }

                            submission.grading.feedback = feedback;
                            if (criterion_feedback) {
                                submission.criterion_feedback = criterion_feedback;
                            }
                            if (commentary) {
                                submission.grading.commentary = commentary;
                            }
                            if (submission.grading.inline_comments) {
                                let toInlineCommentedPdf = [];
                                let pdfsWithInlineComments = [];
                                const files = submission.files.filter((file) => file.type === 'application/pdf');
                                for (const file of files) {
                                    toInlineCommentedPdf.push({
                                        file: file,
                                        comments: submission.grading.inline_comments.filter(
                                            (c) => c.file_id === file.id && c.active
                                        ),
                                    });
                                }
                                if (toInlineCommentedPdf.length) {
                                    await Promise.all(
                                        toInlineCommentedPdf.map(async (item) => {
                                            const fileUrl = getS3FileURL(item.file);
                                            const pdfWithInlineComments = await convertPDFwithInlineComments(
                                                fileUrl,
                                                item.comments
                                            );
                                            pdfsWithInlineComments.push(pdfWithInlineComments);
                                        })
                                    );
                                }
                                if (pdfsWithInlineComments.length) {
                                    submission.pdfs_with_inline_comments = pdfsWithInlineComments;
                                }
                            }

                            // Await the pushGradeToLMS function with retry logic
                            await pushGradeToLMS(accessToken, course_id, submission, assignment, hasOutOfZero, tries);

                            // Update progress
                            setProgress((current) => current + 1);
                        })
                    );
                }

                setFilteredLoading(false);
                setIsSucceed(true);
                setFilteredSubmissions(null);
                message.success(
                    t('common:message.grades-push-success', 'All grades have been successfully pushed to LMS.')
                );
            } catch (e) {
                console.error('Error pushing grades to LMS:', e);
                setFilteredLoading(false);

                message.error(
                    t('common:message.grades-push-fail', 'Failed to push some grades. Please try again later.')
                );
            }
        }
    };
    useEffect(() => {
        if (progress > 0) {
            const percentage = Math.round((progress / submissions.length) * 100);
            setPercentage(percentage);
        }
    }, [progress, submissions?.length]);

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            {isSucceed ? (
                <Stack alignItems={'center'} justifyContent={'center'}>
                    <CustomImageStack alignItems={'center'} justifyContent={'center'}>
                        <svg
                            width='250'
                            height='120'
                            viewBox='0 0 250 120'
                            fill='none'
                            xmlns='http://www.w3.org/2000/svg'
                        >
                            <g clipPath='url(#clip0_7616_84953)'>
                                <path
                                    fillRule='evenodd'
                                    clipRule='evenodd'
                                    d='M64.5 97H155.5C156.015 97 156.517 96.9444 157 96.8389C157.483 96.9444 157.985 97 158.5 97H210.5C214.366 97 217.5 93.866 217.5 90C217.5 86.134 214.366 83 210.5 83H204.5C200.634 83 197.5 79.866 197.5 76C197.5 72.134 200.634 69 204.5 69H223.5C227.366 69 230.5 65.866 230.5 62C230.5 58.134 227.366 55 223.5 55H201.5C205.366 55 208.5 51.866 208.5 48C208.5 44.134 205.366 41 201.5 41H137.5C141.366 41 144.5 37.866 144.5 34C144.5 30.134 141.366 27 137.5 27H80.5C76.634 27 73.5 30.134 73.5 34C73.5 37.866 76.634 41 80.5 41H40.5C36.634 41 33.5 44.134 33.5 48C33.5 51.866 36.634 55 40.5 55H65.5C69.366 55 72.5 58.134 72.5 62C72.5 65.866 69.366 69 65.5 69H25.5C21.634 69 18.5 72.134 18.5 76C18.5 79.866 21.634 83 25.5 83H64.5C60.634 83 57.5 86.134 57.5 90C57.5 93.866 60.634 97 64.5 97ZM227.5 97C231.366 97 234.5 93.866 234.5 90C234.5 86.134 231.366 83 227.5 83C223.634 83 220.5 86.134 220.5 90C220.5 93.866 223.634 97 227.5 97Z'
                                    fill='#EFEDFF'
                                />
                                <path
                                    fillRule='evenodd'
                                    clipRule='evenodd'
                                    d='M114.619 75.3066C114.54 75.8599 114.5 76.4252 114.5 77C114.5 83.6274 119.873 89 126.5 89C133.127 89 138.5 83.6274 138.5 77C138.5 76.4252 138.46 75.8599 138.381 75.3066H167.5V102C167.5 103.657 166.157 105 164.5 105H88.5C86.8431 105 85.5 103.657 85.5 102V75.3066H114.619Z'
                                    fill='white'
                                />
                                <path
                                    fillRule='evenodd'
                                    clipRule='evenodd'
                                    d='M139.5 75C139.5 82.1797 133.68 88 126.5 88C119.32 88 113.5 82.1797 113.5 75C113.5 74.7674 113.506 74.5362 113.518 74.3066H85.5L95.0604 46.0389C95.4726 44.8202 96.6159 44 97.9023 44H155.098C156.384 44 157.527 44.8202 157.94 46.0389L167.5 74.3066H139.482C139.494 74.5362 139.5 74.7674 139.5 75Z'
                                    fill='white'
                                />
                                <path
                                    fillRule='evenodd'
                                    clipRule='evenodd'
                                    d='M137.598 75.9545C137.598 81.5025 132.629 87 126.5 87C120.371 87 115.402 81.5025 115.402 75.9545C115.402 75.7748 115.408 74.5962 115.418 74.4188H94.5L102.661 54.5755C103.013 53.6338 103.989 53 105.087 53H147.913C149.011 53 149.987 53.6338 150.339 54.5755L158.5 74.4188H137.582C137.592 74.5962 137.598 75.7748 137.598 75.9545Z'
                                    fill='#D3CCFF'
                                />
                                <path
                                    fillRule='evenodd'
                                    clipRule='evenodd'
                                    d='M86.75 74.5123V101C86.75 101.966 87.5335 102.75 88.5 102.75H164.5C165.466 102.75 166.25 101.966 166.25 101V74.5123L156.755 46.4393C156.515 45.7285 155.848 45.25 155.098 45.25H97.9023C97.1519 45.25 96.485 45.7285 96.2446 46.4393L86.75 74.5123Z'
                                    stroke='#6347FF'
                                    strokeWidth='2.5'
                                />
                                <path
                                    d='M99.5005 74C103.437 74 107.685 74 112.245 74C114.122 74 114.122 75.3186 114.122 76C114.122 82.6274 119.618 88 126.398 88C133.178 88 138.674 82.6274 138.674 76C138.674 75.3186 138.674 74 140.55 74H165.5M92.0742 74H94.5005H92.0742Z'
                                    stroke='#6347FF'
                                    strokeWidth='2.5'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                                <path
                                    d='M125.6 17V33.7559M99.5 21.3027L110.6 33.7559M151.6 21.3027L140.5 33.7559'
                                    stroke='#6347FF'
                                    strokeWidth='2.5'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                />
                            </g>
                            <defs>
                                <clipPath id='clip0_7616_84953'>
                                    <rect width='250' height='120' fill='white' />
                                </clipPath>
                            </defs>
                        </svg>
                        <CustomCloseButton kind={'exit'} size={'medium'} variant={'square'} onClick={handleClose} />
                    </CustomImageStack>
                    <Stack
                        sx={{ width: '360px', padding: 0 }}
                        alignItems={'center'}
                        justifyContent={'center'}
                        spacing={2}
                    >
                        <Text sx={{ textAlign: 'center' }} component={'h1'} variant={'h6'} weight={'semibold'}>
                            {t(
                                'rubric.push-lms.pushed-success-title',
                                'Grades and Feedback Successfully Pushed to Your LMS'
                            )}
                        </Text>
                        <Text sx={{ textAlign: 'center' }}>
                            {t(
                                'rubric.push-lms.pushed-success-description',
                                'Confirmed grades and/or feedback will now be reflected on your LMS. You can now close this window.'
                            )}
                        </Text>
                    </Stack>
                    <Stack sx={{ padding: '28px 0px 0px 0px' }}>
                        <Button onClick={handleClose}>{t('common:controls.ok', 'Ok')}</Button>
                    </Stack>
                </Stack>
            ) : (
                <Stack spacing={4}>
                    <TitleStack spacing={2}>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Text component={'h1'} variant={'h6'} weight={'semibold'}>
                                {t(
                                    'rubric.push-lms.pushed-confirmation-title',
                                    'Push Confirmed Grades and Feedback to your LMS'
                                )}
                            </Text>
                            <IconButton kind={'exit'} size={'medium'} variant={'square'} onClick={handleClose} />
                        </Stack>
                        <Text>
                            <Trans
                                i18nKey='rubric.push-lms.pushed-confirmation-description'
                                values={{ count: submissions?.length }}
                                components={{
                                    semibold: <Text component='span' weight='semibold' />,
                                }}
                            >
                                TimelyGrader that are ready to be passed back to your LMS. Please keep this window open
                                until the process completes, which may take a few minutes. We found{' '}
                                <semibold>{submissions?.length} graded submissions on</semibold> TimelyGrader that are
                                ready to be passed back to your LMS. Please keep this window open until the process
                                completes, which may take a few minutes.
                            </Trans>
                        </Text>
                        <CustomBox>
                            <CustomText weight={'regular'}>
                                <Text component={'span'} color={'inherit'} weight={'medium'}>
                                    {t('common:ui.note', 'NOTE').toUpperCase()}:{' '}
                                </Text>
                                {t(
                                    'rubric.push-lms.pushed-confirmation-note',
                                    'Grades may be made available to students immediately, depending on the posting policy set on your LMS.'
                                )}
                            </CustomText>
                        </CustomBox>
                    </TitleStack>
                    <BodyStack>
                        {loading ? (
                            <Stack spacing={1}>
                                <Text color={'light'}>
                                    {t('remaining-assignment-count', '{{assignmentStatus}} assignments', {
                                        assignmentStatus: `${progress}/${filteredSubmisisons ? filteredSubmisisons.length : submissions?.length}`,
                                    })}
                                </Text>
                                <ProgressBar value={percentage} />
                            </Stack>
                        ) : (
                            <Stack spacing={4}>
                                <Text>{t('rubric.push-lms.proceed-title', 'How would you like to proceed?')}</Text>
                                <Text>
                                    <Text component={'span'} weight={'semibold'}>
                                        {t('rubric.push-lms.push-all-grades', '1. Push all grades')}
                                    </Text>{' '}
                                    {t(
                                        'rubric.push-lms.push-all-grades-description',
                                        '– This will replace any existing grades on your LMS with the grades from TimelyGrader.'
                                    )}
                                </Text>
                                <Text>
                                    <Text component={'span'} weight={'semibold'}>
                                        {t('rubric.push-lms.push-missing-grades', '2. Push missing grades')}
                                    </Text>{' '}
                                    {t(
                                        'rubric.push-lms.push-missing-grades-description',
                                        '– Only push grades for submissions that do not currently have a grade on your LMS.'
                                    )}
                                </Text>
                            </Stack>
                        )}
                    </BodyStack>
                    <BottomStack alignItems={'flex-end'} direction={'row'} justifyContent={'flex-end'} spacing={4}>
                        <Button
                            loading={filteredLoading}
                            disabled={loading}
                            variant={'secondary'}
                            onClick={handleClickPushMissingGrades}
                        >
                            {t('common:controls.push-missing-grades', 'Push Missing Grades')}
                        </Button>
                        <Button loading={loading} disabled={filteredLoading} onClick={handleClickPush}>
                            {t('common:controls.push-all-grades', 'Push All Grades')}
                        </Button>
                    </BottomStack>
                </Stack>
            )}
        </CustomResponsiveDialog>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(({ theme }) => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '680px',
            padding: theme.spacing(7),
            justifyContent: 'space-between',
        },
    };
});

const CustomImageStack = styled(Stack)(() => {
    return {
        position: 'relative',
        width: '100%',
    };
});

const CustomCloseButton = styled(IconButton)(() => {
    return {
        position: 'absolute',
        top: 0,
        right: 0,
    };
});

const TitleStack = styled(Stack)(() => {
    return {};
});

const BodyStack = styled(Stack)(() => {
    return {};
});

const BottomStack = styled(Stack)(() => {
    return {};
});

const CustomBox = styled(Box)(({ theme }) => {
    return {
        borderRadius: '8px',
        padding: theme.spacing(2, 3),
        background: theme.palette.ensightful.warning[100],
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        color: theme.palette.ensightful.warning[900],
    };
});

export default ConfirmPushGradesDialog;
